import {
  CutlistOrder,
  CutlistRequestType,
  FindOrCreateUserLeadResponse,
  Material,
} from '@cutr/constants/cutlist';
import { BackendCutlistGroup } from '@cutr/constants/cutlist-backend';
import { CutlistFileImport, FileUpload } from '@cutr/constants/cutlist-llm.ts';
import type { NestedCut } from '@cutr/nesting';
import * as Sentry from '@sentry/react';

import i18n from '@/i18n';
import { getTicker, isCutrEmbedded, isStarterTier } from '@/theme';
import {
  createBackendGrouping,
  createBackendGroupingForNesting,
} from '@/utils/backend-compatibility';
import { pick } from '@/utils/misc';

import { useLeadDetails } from '../account';
import { Address, useDeliveryAddress } from '../address';
import { dotCutlist } from '../importExport';
import {
  agentLoggedInSelector,
  AuthState,
  isLoggedInSelector,
  useAuthStore,
} from '../login';
import { useMaterialGroupState } from '../materialsGroup';
import { useNestingStore } from '../nesting';
import { useCutlistState } from '../store';

const CUTLIST_FIELDS = [
  'title',
  'hasMaterials',
  'notes',
  'customerReference',
  'deliverLeftoverMaterials',
  'requestedDeliveryDate',
  'discountAmount',
  'discountPercentage',
  'markupAmount',
  'markupPercentage',
  'currency',
  'exchangeRate',
  'thirdPartyId',
];
const USER_LEAD_DETAILS_FIELDS = [
  'name',
  'companyName',
  'phone',
  'clientNumber',
];

export type NestingBatchPriceResult = {
  articleCode: string;
  sheetsNeeded: number;
  materialCost: number;
  totalCost: number;
  materialLostM2: number;
};

export type NestingBatchPriceResponse = {
  winningArticleCode: string;
  results: NestingBatchPriceResult[];
};

export type ProductionPart = {
  ref: string;
  label: string;
  lengthMM: number;
  widthMM: number;
  quantity: number;
};

export type ProductionLayout = {
  lengthMM: number;
  widthMM: number;
  count: number;
  articleCode?: string;
  materialDescription: string;
  cuts: NestedCut[];
  placements: {
    ref: string;
    x: number;
    y: number;
    lengthMM: number;
    widthMM: number;
    fontSize: number;
    dimensions: string | null;
  }[];
  parts: ProductionPart[];
};

export type ProductionPlan = {
  id: string;
  shortId: string;
  title: string | null;
  kerfMM?: number;
  groups: { layouts: ProductionLayout[] }[];
};

export class ApiError extends Error {
  response: { status: number; data: unknown };

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';

    this.response = { status, data };
  }
}

export const checkError = async (res: Response, redirectToLogin = true) => {
  if (!res.ok) {
    if (redirectToLogin && res.status === 401) {
      localStorage.removeItem('cutlist-auth');
      window.location.href = `/login?redirect=${encodeURIComponent(
        window.location.pathname + window.location.search
      )}`;
      return new Promise<Response>(() => {});
    }

    const json = await res.json().catch(() => ({}));

    if (json.statusCode === 502) {
      const errors = json.errors || [];
      // @ts-expect-error - error handling
      console.error(errors.map((e) => e.message).join('\n\n'));
      throw new ApiError(json.message || res.statusText, res.status, {
        errors,
      });
    }

    throw new ApiError(json.message || res.statusText, res.status, json);
  }

  return res;
};

export const logError = (err: Error) => {
  Sentry.captureException(
    err,
    // @ts-expect-error Own error type
    { extra: err.response?.data }
  );
  throw err;
};

const baseUrl = import.meta.env.VITE_API_BASE_URL;

export const isAuthorized = () => {
  const ticker = getTicker();
  const isLoggedIn = isLoggedInSelector(useAuthStore.getState());

  if (ticker === 'CUTR-EMBED') return true;

  return isLoggedIn;
};

export const isAuthorizedAgent = () => {
  const isAgentLoggedIn = agentLoggedInSelector(useAuthStore.getState());

  return isAgentLoggedIn;
};

const beamMaterialsCache: Record<string, Material[]> = {};

export const api = {
  createBaseUrl(resource = '', basePath = '') {
    return new URL(resource, baseUrl + basePath);
  },
  createUrl(path = '', includeConfig = false) {
    const url = this.createBaseUrl(path, '/v2/cutlist/');

    if (includeConfig) {
      if (i18n.resolvedLanguage) {
        url.searchParams.append('lng', i18n.resolvedLanguage);
      }

      const ticker = getTicker();
      const auth = useAuthStore.getState();

      url.searchParams.append('source', ticker);
      if (auth.token) {
        url.searchParams.append('token', auth.token);
      }
    }

    return url;
  },
  getHeaders({ includeAuth = true } = {}): Record<string, string> {
    const auth = useAuthStore.getState();

    const ticker = getTicker();
    const headers: Record<string, string> = {
      'X-Cutlist-Source': ticker,
    };

    if (i18n.resolvedLanguage) {
      headers['Accept-Language'] = i18n.resolvedLanguage;
    }

    if (includeAuth && auth.token) {
      headers['Authorization'] = `Bearer ${auth.token}`;
    }

    return headers;
  },
  async materials() {
    const canAccessPrivateMaterials = isAuthorizedAgent() || isAuthorized();

    const url = canAccessPrivateMaterials
      ? 'materials'
      : 'unauthenticated/materials';

    return fetch(this.createUrl(url), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  async beamMaterials({
    profileWidthMM,
    profileThicknessMM,
    limit,
  }: {
    profileWidthMM?: number;
    profileThicknessMM?: number;
    limit?: number;
  }) {
    const cacheKey = [profileWidthMM, profileThicknessMM, limit].join('-');

    if (beamMaterialsCache[cacheKey]) {
      return beamMaterialsCache[cacheKey];
    }

    const params = new URLSearchParams();
    if (profileWidthMM) {
      params.set('profileWidthMM', profileWidthMM.toString());
    }
    if (profileThicknessMM) {
      params.set('profileThicknessMM', profileThicknessMM.toString());
    }
    if (limit) {
      params.set('limit', limit.toString());
    }

    return fetch(this.createUrl(`materials/beams?${params}`), {
      headers: { ...api.getHeaders() },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => {
        beamMaterialsCache[cacheKey] = data;
        return data;
      })
      .catch(logError);
  },
  pricing(cutlistGroups: BackendCutlistGroup[], clientNumber?: string) {
    if (isCutrEmbedded()) {
      return this.pricingEmbeddedCutlist(cutlistGroups);
    }

    const body = JSON.stringify({
      cutlistGroups,
      userLeadDetails: { clientNumber },
      finalise: false,
    });
    return fetch(this.createUrl(), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  pricingEmbeddedCutlist(cutlistGroups: BackendCutlistGroup[]) {
    const { orderId } = useCutlistState.getState();
    const body = JSON.stringify({
      cutlistGroups,
      cutlistId: orderId,
    });
    return fetch(this.createUrl(`embedded/${orderId}/price/`), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  nestingBatchPrice(
    cutlistId: string,
    candidates: { articleCode: string; cutlistGroup: BackendCutlistGroup }[]
  ): Promise<NestingBatchPriceResponse> {
    return fetch(this.createUrl(`${cutlistId}/nesting-batch-price`), {
      method: 'POST',
      body: JSON.stringify({ candidates }),
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  async updateUserLeadLocale() {
    const authState = useAuthStore.getState();
    if (!authState.email) {
      return;
    }
    const body = JSON.stringify({ ...authState });
    fetch(this.createUrl('lead/locale', true), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
  saveEmbeddedCutlist(id = '', requestType: CutlistRequestType) {
    return fetch(this.createUrl(`embedded/${id}`), {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify({ ...serializeCutlist(), requestType }),
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
      credentials: 'include',
    })
      .then(checkError)
      .catch(logError);
  },
  async getCutlistFileImport(
    cutlistId: string,
    signal?: AbortSignal
  ): Promise<CutlistFileImport> {
    return fetch(api.createUrl(`file-import/${cutlistId}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
      credentials: 'include',
      signal,
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getCutlist(id: string): Promise<CutlistOrder> {
    return fetch(this.createUrl(`${id}`), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
      credentials: 'include',
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data)
      .catch(logError);
  },
  getProductionPlan(id: string): Promise<ProductionPlan> {
    return fetch(this.createUrl(`production/${id}`), {
      headers: api.getHeaders({ includeAuth: false }),
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => data);
  },
  updateCutlistTitle(id: string, title: string) {
    return fetch(this.createUrl(`${id}/title`), {
      method: 'PATCH',
      body: JSON.stringify({ title }),
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
  getClientDetails(clientNumber: string, email: string, source: string) {
    const body = JSON.stringify({ email, source, clientNumber });
    return fetch(this.createUrl('client-details'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json() as unknown)
      .catch(logError);
  },
  login(
    clientNumber: string,
    email: string,
    source: string,
    cutlistId?: string
  ): Promise<{
    email: string;
    token: string;
    clientNumber: string;
    userLeadId: string;
  }> {
    const body = JSON.stringify({ email, source, clientNumber, cutlistId });
    return fetch(this.createUrl('login'), {
      method: 'POST',
      body,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then((data) => {
        window.analytics.identify(data.userLeadId, {
          email,
          clientNumber,
          ticker: source,
        });
        if (data.locale) {
          i18n.changeLanguage(data.locale);
        }
        return data as {
          email: string;
          token: string;
          clientNumber: string;
          userLeadId: string;
        };
      })
      .catch(logError);
  },
  devLogin() {
    return fetch(this.createUrl('dev-login'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then((data) => {
        if (data.locale) {
          i18n.changeLanguage(data.locale);
        }
        return data as unknown;
      })
      .catch(logError);
  },
  loginByKey(key: string) {
    const body = JSON.stringify({ key });
    return fetch(this.createUrl('loginByKey'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders({ includeAuth: false }),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then((data) => {
        if (data.locale) {
          i18n.changeLanguage(data.locale);
        }
        return data as unknown;
      })
      .catch(logError);
  },
  thirdPartyLogin(token: string) {
    const body = JSON.stringify({ token });
    return fetch(this.createUrl('third-party-login'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders({ includeAuth: false }),
      },
    })
      .then((res) => checkError(res, false))
      .then((res) => res.json())
      .then(({ data }) => {
        return data as AuthState & {
          cutlistId: string;
          missingArticleCodes: string[];
        };
      })
      .catch(logError);
  },
  loginByToken(token: string): Promise<AuthState> {
    const body = JSON.stringify({ token });
    return fetch(this.createUrl('loginByToken'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders({ includeAuth: false }),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then((data) => {
        if (data.locale) {
          i18n.changeLanguage(data.locale);
        }
        return data;
      })
      .catch(logError);
  },
  loginBySession(): Promise<AuthState> {
    return fetch(this.createUrl('login-by-session'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders({ includeAuth: false }),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
  parseCutlistCsv(data: string) {
    const language = i18n.resolvedLanguage;
    const body = JSON.stringify({ data, language });
    return fetch(this.createUrl('csv'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json() as unknown)
      .catch(logError);
  },
  findOrCreateUserLead(
    email: string,
    cutlistId: string
  ): Promise<FindOrCreateUserLeadResponse> {
    const body = JSON.stringify({ email, cutlistId });
    return fetch(this.createUrl('unauthenticated/lead'), {
      method: 'POST',
      body,
      headers: {
        'Content-Type': 'application/json',
        ...api.getHeaders(),
      },
    })
      .then(checkError)
      .then((res) => res.json())
      .then(({ data }) => {
        return data as FindOrCreateUserLeadResponse;
      })
      .catch(logError);
  },
  async deleteCutlistFileImportFile(
    cutlistId: string,
    uri: string
  ): Promise<Response> {
    return fetch(api.createUrl(`file-import/${cutlistId}`), {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
      body: JSON.stringify({ uri }),
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
  async triggerLlmFlow(cutlistId: string): Promise<Response> {
    return fetch(api.createUrl(`file-import/${cutlistId}/trigger-llm-flow`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...api.getHeaders() },
    })
      .then(checkError)
      .then((res) => res.json())
      .catch(logError);
  },
  async uploadFileImport(
    file: File,
    cutlistId: string,
    onProgress: (progress: number) => void
  ): Promise<FileUpload> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const url = api.createUrl(`file-import/${cutlistId}`);
      const headers = api.getHeaders();

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url.toString());

      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText).data);
        } else {
          reject(new Error(xhr.statusText));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Network error')));

      xhr.send(formData);
    });
  },
};

function getAddresses(deliveryAddress: Address) {
  const addresses = [];

  addresses.push(getAddress(deliveryAddress));

  return addresses.filter(Boolean);
}

export function getAddress(a: Address) {
  if (isEmpty(a)) return;
  return { ...a, name: a.contactName };
}

const isEmpty = (a: Address) => {
  return a.contactName === '' && a.line1 === '' && a.postalCode === '';
};

export function serializeCutlist() {
  const ticker = getTicker();
  const detailsState = useLeadDetails.getState();
  const cutlistState = useCutlistState.getState();
  const { sheetGroups } = useNestingStore.getState();
  const groupsState = useMaterialGroupState.getState();
  const deliveryAddress = useDeliveryAddress.getState();
  const { clientNumber } = useAuthStore.getState();
  const dotCutlistFileContent = JSON.stringify(
    dotCutlist(cutlistState, groupsState)
  );

  const cutlistAddresses = getAddresses(deliveryAddress);

  const cutlistGroups = isStarterTier()
    ? createBackendGroupingForNesting(groupsState.groups)
    : createBackendGrouping(sheetGroups, groupsState.groups);
  const cutlist = pick(cutlistState, CUTLIST_FIELDS) as Partial<
    typeof cutlistState
  >;
  const userLeadDetails = pick(
    detailsState,
    USER_LEAD_DETAILS_FIELDS
  ) as typeof detailsState;

  const data = {
    ...cutlist,
    discountPercentage: cutlist.discountPercentage ?? null,
    discountAmount: cutlist.discountAmount ?? null,
    markupPercentage: cutlist.markupPercentage ?? null,
    markupAmount: cutlist.markupAmount ?? null,
    email: detailsState.email,
    addresses: cutlistAddresses,
    userLeadDetail: {
      ...userLeadDetails,
      phone: deliveryAddress.phone,
      name: userLeadDetails.name,
      clientNumber,
    },
    cutlistGroups,
    finalise: true,
    dotCutlistFileContent,
    requestedDeliveryDate: cutlist.requestedDeliveryDate || null,
  };
  return { ...data, source: ticker };
}

export function cutlistUpdateBodyData() {
  const createBodyData = serializeCutlist();
  // remove some of the fields that are not needed for update
  return {
    ...createBodyData,
    finalise: undefined,
    email: undefined,
    userLeadDetails: {
      ...createBodyData.userLeadDetail,
      clientNumber: undefined,
      email: undefined,
    },
  };
}
